<script setup lang="ts">
  import { useRouteQuery } from '@vueuse/router'
  import { computed, onMounted, type HTMLAttributes } from 'vue'
  import { useSupportSessionContext } from '../../composables/useSupportSession'
  import type {
    GetSupporSessionsHistoryQuery,
    GetSupporSessionsHistoryQueryParams
  } from '@gat-ship/types'

  import { globalUtils, useFormatting } from '@gat-ship/utils'
  import {
    ComboboxField,
    DatePicker,
    Button,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow
  } from '@gat-ship/vue-components'
  import SupportSessionCellActions from './SupportSessionCellActions.vue'

  const props = defineProps<{
    class?: HTMLAttributes['class']
  }>()

  const { formatDate } = useFormatting()

  const supportSessionService = useSupportSessionContext()!

  const data = computed(
    () => supportSessionService.historySessionsDataSource.data.value
  )

  const localDate = globalUtils.functions.fromNow({ months: -6 })
  const today = globalUtils.functions.dateNow()

  const status = useRouteQuery<GetSupporSessionsHistoryQuery['status']>(
    'status',
    'active'
  )

  const fromDate = useRouteQuery<GetSupporSessionsHistoryQuery['from']>(
    'fromDate',
    localDate
  )

  const toDate = useRouteQuery<GetSupporSessionsHistoryQuery['to']>(
    'toDate',
    today
  )

  const searchFilterStatusOptions: {
    title: string
    value: Required<GetSupporSessionsHistoryQuery>['status']
  }[] = [
    { title: 'All Sessions', value: 'any' },
    { title: 'All Active Sessions', value: 'active' },
    { title: 'Between Date Range', value: 'historical' }
  ]

  function getQueryParams(
    query: GetSupporSessionsHistoryQuery
  ): GetSupporSessionsHistoryQueryParams {
    if (query.status === 'historical' && query.from && query.to) {
      return {
        status: 'historical',
        from: query.from,
        to: query.to
      }
    }
    return {
      status: query.status ?? 'any'
    } as GetSupporSessionsHistoryQueryParams
  }

  async function onSearch() {
    const query = getQueryParams({
      from: fromDate.value,
      to: toDate.value,
      status: status.value
    })
    await supportSessionService.historySessionsDataSource.fetchData({
      query,
      forceFetch: true
    })
  }

  onMounted(async () => {
    await onSearch()
  })
</script>

<template>
  <div class="flex items-end gap-3">
    <div class="w-full max-w-[180px]">
      <ComboboxField
        id="support-history-status"
        v-model="status"
        label="Show"
        :items="searchFilterStatusOptions"
        required
        @update:model-value="
          () => {
            onSearch()
          }
        "
      />
    </div>
    <DatePicker
      v-model="fromDate"
      :disabled="status !== 'historical'"
      anchor-time="midnight"
      label="Granted From"
      :class="['max-w-[200px]', { 'opacity-60': status !== 'historical' }]"
    />
    <DatePicker
      v-model="toDate"
      :disabled="status !== 'historical'"
      anchor-time="midnight"
      label="Granted To"
      :class="['max-w-[200px]', { 'opacity-60': status !== 'historical' }]"
    />
    <Button @click="onSearch">Search</Button>
  </div>
  <Table :class="props.class">
    <TableHeader>
      <TableRow>
        <TableHead>Granted From</TableHead>
        <TableHead>Granted To</TableHead>
        <TableHead>Requested By</TableHead>
        <TableHead class="w-10">
          <span class="sr-only">Actions</span>
        </TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <TableEmpty v-if="data.length === 0" :colspan="4">
        No sessions found.
      </TableEmpty>
      <TableRow v-for="session in data" :key="session.sessionId">
        <TableCell>{{ formatDate(session.grantedFrom) }}</TableCell>
        <TableCell>{{ formatDate(session.grantedTo) }}</TableCell>
        <TableCell>{{ session.requestedByFullName }}</TableCell>
        <TableCell class="text-right">
          <SupportSessionCellActions :session="session" />
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</template>
